import { useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../../client';
import { useDraftMutation } from '../helpers/draftMutation';
import { stripStableIdsFromInputs } from '../helpers/draftGraphOperations';
import {
  applyDraftCache,
  optimisticDraftUpdate,
  restoreDraftCache
} from '../helpers/draftCacheHelpers';
import { reconcileEnvelope } from '../../draftGraph';
import {
  type DraftNodeEnvelope,
  type ProcessDraftAssignment,
  type RestoreDraftAssignmentRequest,
  type UpdateDraftAssignmentRequest
} from '@bliks-io/contracts';
import { basePath } from './shared';

/**
 * PATCH /draft/assignments/:stableStepId — partial patch of assignee/inputs/
 * helpers/whoElseDoesThis/generated fields on a draft assignment.
 */
export function useUpdateDraftAssignment(processId: string) {
  const queryClient = useQueryClient();
  return useDraftMutation(processId, {
    mutationFn: async ({
      stableStepId,
      patch
    }: {
      stableStepId: string;
      patch: UpdateDraftAssignmentRequest;
    }): Promise<DraftNodeEnvelope> => {
      const { data } = await apiClient.patch<DraftNodeEnvelope>(
        `${basePath(processId)}/assignments/${stableStepId}`,
        patch
      );
      return data;
    },
    onMutate: async ({ stableStepId, patch }) => {
      return optimisticDraftUpdate(queryClient, processId, (old) => ({
        ...old,
        draftAssignments: old.draftAssignments.map((a) =>
          String(a.stableStepId) === stableStepId
            ? ({ ...a, ...(patch as object) } as ProcessDraftAssignment)
            : a
        )
      }));
    },
    onError: (_err, _vars, ctx) => {
      restoreDraftCache(queryClient, processId, ctx);
    },
    onSuccess: (envelope) => {
      applyDraftCache(queryClient, processId, (old) => reconcileEnvelope(old, envelope));
    }
  });
}

/**
 * DELETE /draft/assignments/:stableStepId — removes a draft assignment and
 * strips dangling references from downstream inputs (both collections).
 */
export function useDeleteDraftAssignment(processId: string) {
  const queryClient = useQueryClient();
  return useDraftMutation(processId, {
    mutationFn: async (stableStepId: string): Promise<DraftNodeEnvelope> => {
      const { data } = await apiClient.delete<DraftNodeEnvelope>(
        `${basePath(processId)}/assignments/${stableStepId}`
      );
      return data;
    },
    onMutate: async (stableStepId) => {
      return optimisticDraftUpdate(queryClient, processId, (old) => ({
        ...old,
        draft: { ...old.draft, updatedAt: new Date() },
        draftAssignments: old.draftAssignments.filter(
          (a) => String(a.stableStepId) !== stableStepId
        ),
        steps: old.steps.map((s) => stripStableIdsFromInputs(s, new Set([stableStepId])))
      }));
    },
    onError: (_err, _vars, ctx) => {
      restoreDraftCache(queryClient, processId, ctx);
    },
    onSuccess: (envelope) => {
      applyDraftCache(queryClient, processId, (old) => reconcileEnvelope(old, envelope));
    }
  });
}

/**
 * Recreate a previously-deleted draft assignment. Used exclusively by the
 * undo path for `useDeleteDraftAssignment` — the caller snapshots the assignment
 * before deletion, then replays that snapshot here.
 */
export function useRestoreDraftAssignment(processId: string) {
  const queryClient = useQueryClient();
  return useDraftMutation(processId, {
    mutationFn: async ({
      stableStepId,
      body
    }: {
      stableStepId: string;
      body: RestoreDraftAssignmentRequest;
    }): Promise<DraftNodeEnvelope> => {
      const { data } = await apiClient.post<DraftNodeEnvelope>(
        `${basePath(processId)}/assignments/${stableStepId}/restore`,
        body
      );
      return data;
    },
    onSuccess: (envelope) => {
      applyDraftCache(queryClient, processId, (old) => reconcileEnvelope(old, envelope));
    }
  });
}

/**
 * Convert a draft assignment back into a manual draft step. The assignee is
 * stashed so a round-trip preserves both sides. The node's `stableStepId` is
 * preserved across the conversion, so the envelope's kind-swap routing moves
 * it from `draftAssignments` into `steps` while edges in/out keep pointing
 * to it.
 */
export function useConvertAssignmentToStep(processId: string) {
  const queryClient = useQueryClient();
  return useDraftMutation(processId, {
    mutationFn: async ({ stableStepId }: { stableStepId: string }): Promise<DraftNodeEnvelope> => {
      const { data } = await apiClient.post<DraftNodeEnvelope>(
        `${basePath(processId)}/assignments/${stableStepId}/convert-to-step`
      );
      return data;
    },
    onSuccess: (envelope) => {
      applyDraftCache(queryClient, processId, (old) => reconcileEnvelope(old, envelope));
    }
  });
}
